import * as THREE from 'three'
import { FlyControls as ThreeFlyControls } from 'three/addons/controls/FlyControls.js'
import { FIRST_PERSON_LOOK_SPEED, FIRST_PERSON_MOVEMENT_SPEED } from '@/shared/constants'
import { CameraMode } from '@/shared/types/core'
import type { NativeControlServices } from './NativeControlUtils'

/** FlyController 构造参数 */
export interface FlyControllerOptions {
  /** 控制器共享服务集合 */
  services: NativeControlServices
}

const THREE_FLY_ROLL_SPEED_MULTIPLIER = 10

/** Fly mode powered by Three's official FlyControls. */
export class FlyController extends ThreeFlyControls {
  private readonly services: NativeControlServices
  private readonly controlCamera: THREE.PerspectiveCamera

  constructor(options: FlyControllerOptions) {
    const camera = new THREE.PerspectiveCamera()
    super(camera)

    this.services = options.services
    this.controlCamera = camera
    this.enabled = false
    this.dragToLook = false
    this.autoForward = false
    this.movementSpeed = FIRST_PERSON_MOVEMENT_SPEED
    this.rollSpeed = FIRST_PERSON_LOOK_SPEED * THREE_FLY_ROLL_SPEED_MULTIPLIER
  }

  /**
   * 连接 DOM 元素并切换到透视相机。
   *
   * @param element - 承载交互事件的 HTML 元素
   */
  override connect(element: HTMLElement): void {
    if (this.services.rig.mode !== CameraMode.Perspective) {
      const state = this.services.rig.getState()
      this.services.rig.setState({
        ...state,
        mode: CameraMode.Perspective,
      })
    }

    this.syncProxyFromRig()
    super.connect(element)
    this.controlCamera.updateMatrixWorld(true)
  }

  /**
   * 每帧更新控制器。
   *
   * @param delta - 自上一帧的时间间隔，单位秒
   */
  override update(delta: number): void {
    if (!this.enabled) return

    this.syncProxyFromRig()
    super.update(delta)
    this.controlCamera.updateMatrixWorld(true)
    this.syncRigFromProxy()
  }

  /** 释放控制器资源 */
  override dispose(): void {
    if (this.domElement) {
      super.dispose()
    }
  }

  private syncProxyFromRig(): void {
    const state = this.services.rig.getState()

    this.controlCamera.position.copy(state.position)
    this.controlCamera.up.copy(state.up)
    this.controlCamera.fov = state.fov
    this.controlCamera.near = this.services.rig.near
    this.controlCamera.far = this.services.rig.far
    this.controlCamera.lookAt(state.target)
    this.controlCamera.updateProjectionMatrix()
    this.controlCamera.updateMatrixWorld(true)
  }

  private syncRigFromProxy(): void {
    const previous = this.services.rig.getState()
    const direction = new THREE.Vector3()
    this.controlCamera.getWorldDirection(direction)

    const distance = Math.max(previous.position.distanceTo(previous.target), 1)
    const target = this.controlCamera.position.clone().addScaledVector(direction, distance)
    const up = new THREE.Vector3(0, 1, 0).applyQuaternion(this.controlCamera.quaternion).normalize()

    this.services.rig.setState({
      ...previous,
      position: this.controlCamera.position.clone(),
      target,
      up,
      fov: this.controlCamera.fov,
      mode: CameraMode.Perspective,
    })
  }
}

export default FlyController
